import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import { FormikField, FormikPhoneField } from '../../../components/Fields';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Button from '../../../components/Button';
import ContextSnackbar from '../../../components/Snackbar';
import { VENUES } from 'Queries/SuperAdmin';
import { SnackbarContext } from 'Store/SnackbarContext';
import { subRouteCodes as SUB_ROUTES } from '../../../constants/routes';
import { initialValues, CreateUserSchema } from './helpers';
import { HeadingFive } from 'Components/Headings';
import './Users.scss';
import { FormSelect } from 'Components/Select';
import { USER_ROLES_FOR_LOCAL_CONTEXT } from 'Queries/UserRolesForLocalContext';
import SIGN_UP from 'Mutations/SignUp';

const getOptions = venues => {
  if (!venues) return null;

  return venues.map(venue => {
    return {
      label: `ID: ${venue.id} - ${venue.name}`,
      value: venue.id
    };
  });
};

const CreateUserForm = () => {
  const [venues, setVenues] = useState([]);
  const [roles, setRoles] = useState([]);
  const { data: venuesData } = useQuery(VENUES);
  const { data: rolesData } = useQuery(USER_ROLES_FOR_LOCAL_CONTEXT);
  const { showSnackbar } = useContext(SnackbarContext);

  const [registerUser, { data, loading, error }] = useMutation(SIGN_UP);
  const history = useHistory();

  useEffect(() => {
    if (venuesData) setVenues(venuesData.venues.sort((a, b) => +a.id - +b.id));
  }, [venuesData]);

  useEffect(() => {
    if (rolesData) setRoles(rolesData.userRoles);
  }, [rolesData]);

  useEffect(() => {
    if (data && !data.error) {
      showSnackbar('User created successfully', {
        error: false,
        duration: 5000
      });
      history.push(SUB_ROUTES.SUPER_ADMIN.USERS);
    } else if (error) {
      showSnackbar(error?.message?.replace('GraphQL error: ', '') || `Unable to create User`, { error: true, duration: 5000 });
    }
  }, [data, error]);

  return (
    <div className="create-user-container">
      <ContextSnackbar />
      <Formik
        initialValues={initialValues}
        onSubmit={async values =>
          await registerUser({
            variables: {
              input: {
                venueId: values.venueId,
                firstName: values.firstName,
                lastName: values.lastName,
                unitName: values.unitName,
                email: values.email,
                password: values.password,
                phone: values.phone,
                roleId: values.roleId
              }
            }
          })
        }
        validationSchema={CreateUserSchema}>
        {({ values, setFieldValue, isValid, dirty, isSubmitting }) => {
          return (
            <Form>
              <HeadingFive label="USER INFORMATION" />
              <Field name="venueId" autoComplete="none">
                {({ field, meta }) => (
                  <FormSelect
                    {...field}
                    {...meta}
                    selectedOption={values.venueId}
                    label="VENUE"
                    cb={e => {
                      const venue = venues.filter(venue => venue.id === e.target.value)[0];
                      if (venue) {
                        setFieldValue('venueId', venue.id);
                      }
                    }}
                    className="venue-select"
                    options={getOptions(venues)}
                  />
                )}
              </Field>
              <Field name="roleId" autoComplete="none">
                {({ field, meta }) => (
                  <FormSelect
                    {...field}
                    {...meta}
                    selectedOption={values.roleId}
                    label="ROLE"
                    cb={e => {
                      const role = roles.filter(role => role.id === e.target.value)[0];
                      if (role) {
                        setFieldValue('roleId', role.id);
                      }
                    }}
                    className="role-select"
                    options={getOptions(roles)}
                  />
                )}
              </Field>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field autoComplete="none" label="EMAIL" type="email" name="email" variant="filled" component={FormikField} />
                </Grid>
                <Grid item xs={12}>
                  <Field autoComplete="none" label="PASSWORD" type="text" name="password" variant="filled" component={FormikField} />
                </Grid>
                <Grid item xs={12}>
                  <Field autoComplete="none" label="FIRST NAME" type="text" name="firstName" variant="filled" component={FormikField} />
                </Grid>
                <Grid item xs={12}>
                  <Field autoComplete="none" label="LAST NAME" type="text" name="lastName" variant="filled" component={FormikField} />
                </Grid>
                <Grid item xs={12}>
                  <Field name="phone" autoComplete="none">
                    {({ field, meta }) => (
                      <FormikPhoneField
                        {...field}
                        {...meta}
                        error={meta.touched && meta.error}
                        helperText={meta.touched && !!meta.error && meta.error}
                        label="PHONE"
                        autoComplete="phone"
                        variant="filled"
                        onChange={e => setFieldValue('phone', e.target.value.replace(/[^0-9]/g, ''))}
                        required
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
              <div className="flex-button-wrapper">
                <Button
                  className="form-button"
                  primary
                  variant="contained"
                  size="small"
                  type="submit"
                  disabled={isSubmitting || !dirty || !isValid || loading}
                  data-testid="create-group-save">
                  SAVE
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default CreateUserForm;
